import { useEffect, useReducer, useState } from "react";
import { supabase, setGoUserId } from "@/lib/supabase";
import { Session } from "@supabase/supabase-js";
import { posthog } from "@/lib/posthog";
import { syncClickedFromServer, clearRemoteClicked } from "@/lib/clicked";

// Module-level cache so navigating between pages doesn't reset session state.
// After the first getSession() resolves, subsequent component mounts start
// with loading: false and the cached session — no blank flash between pages.
let _session: Session | null = null;
let _initialized = false;
const _subscribers = new Set<() => void>();

function notify() {
  _subscribers.forEach((fn) => fn());
}

function applyClickIdentity(session: Session | null) {
  setGoUserId(session?.user?.id ?? null);
  if (session?.user) void syncClickedFromServer();
  else clearRemoteClicked();
}

// Kick off the initial session fetch once at module load time.
supabase.auth.getSession().then(({ data: { session } }) => {
  _session = session;
  _initialized = true;
  applyClickIdentity(session);
  notify();
});

// Module-level auth subscription so it stays alive regardless of which
// components are mounted — auth changes are never missed during navigation.
supabase.auth.onAuthStateChange((event, session) => {
  _session = session;
  _initialized = true;
  if (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "SIGNED_OUT") {
    applyClickIdentity(session);
  } else {
    setGoUserId(session?.user?.id ?? null);
  }
  notify();
  if (event === "SIGNED_IN" && session?.user) {
    posthog.identify(session.user.id, { email: session.user.email });
  } else if (event === "SIGNED_OUT") {
    posthog.reset();
  }
});

export function useSession() {
  const [, rerender] = useReducer((x) => x + 1, 0);
  // Read directly from module cache — no useState that resets on mount.
  const [snapshot] = useState(() => ({ session: _session, loading: !_initialized }));

  useEffect(() => {
    // If the module cache updated between mount and this effect, rerender once.
    if (_initialized && snapshot.loading) rerender();
    _subscribers.add(rerender);
    return () => { _subscribers.delete(rerender); };
  }, []);

  return { session: _session, loading: !_initialized };
}
